// src/utils/seedComplianceData.ts
import {
  Resident, Incident, ComplianceTask,
  LocalAuthority, DependencyLevel, IncidentStatus, ActionStatus,
  Audit, TrainingRecord, Policy, Risk, Meeting, EquipmentInspection, Complaint,
  AuditStatus, TrainingStatus, PolicyStatus, RiskSeverity, ComplaintStatus,
  CompetencyActionPlan, PlanStatus
} from '../types/compliance';

const generateId = () => Date.now().toString(36) + Math.random().toString(36).substr(2, 5);

const now = new Date().toISOString();
const past = (days: number) => new Date(Date.now() - days * 86400000).toISOString();
const future = (days: number) => new Date(Date.now() + days * 86400000).toISOString();

export function seedComplianceData() {
  // ─── RESIDENTS ──────────────────────────────────────────
  const residents: Resident[] = [
    {
      id: generateId(),
      identityCode: 'RES-WAK-001',
      localAuthority: LocalAuthority.WAKEFIELD,
      dependencyLevel: DependencyLevel.HIGH,
      dailyCareHoursRequired: 8.5,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: generateId(),
      identityCode: 'RES-LEE-002',
      localAuthority: LocalAuthority.LEEDS,
      dependencyLevel: DependencyLevel.MEDIUM,
      dailyCareHoursRequired: 5.0,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: generateId(),
      identityCode: 'RES-BRA-003',
      localAuthority: LocalAuthority.BRADFORD,
      dependencyLevel: DependencyLevel.LOW,
      dailyCareHoursRequired: 2.0,
      createdAt: now,
      updatedAt: now,
    },
    {
      id: generateId(),
      identityCode: 'RES-KIR-004',
      localAuthority: LocalAuthority.KIRKLEES,
      dependencyLevel: DependencyLevel.COMPLEX,
      dailyCareHoursRequired: 12.0,
      createdAt: now,
      updatedAt: now,
    },
  ];

  // ─── INCIDENTS ──────────────────────────────────────────
  const incidents: Incident[] = residents.map((r, idx) => ({
    id: generateId(),
    incidentRef: `INC-2026-${String(idx+1).padStart(3, '0')}`,
    residentId: r.id,
    resident: r,
    status: idx % 3 === 0 ? IncidentStatus.OPEN : (idx % 3 === 1 ? IncidentStatus.INVESTIGATING : IncidentStatus.AWAITING_NOTIFICATIONS),
    incidentDate: past(2),
    description: `Incident description for ${r.identityCode}: fall with injury.`,
    immediateActionsTaken: `Immediate actions: first aid, alerted nurse, completed incident form.`,
    rootCauseAnalysis: null,
    lessonsLearned: null,
    localAuthorityNotified: false,
    localAuthorityNotifyDate: null,
    localAuthorityRef: null,
    cqcNotified: false,
    cqcNotifyDate: null,
    cqcRef: null,
    familyInformed: false,
    familyInformTime: null,
    closedAt: null,
    closedByManagerSignature: null,
    actionPlanItems: [],
  }));

  // ─── TASKS ──────────────────────────────────────────────
  const tasks: ComplianceTask[] = residents.flatMap((r, idx) => {
    const inc = incidents[idx];
    return [
      {
        id: generateId(),
        incidentId: inc.id,
        incident: inc,
        residentId: r.id,
        resident: r,
        title: `Safeguarding sign-off for ${r.identityCode}`,
        correctiveActionPlan: 'Complete safeguarding review and update care plan.',
        assignedOwner: 'Sarah Mitchell',
        status: ActionStatus.COMPLETED,
        dueDate: past(5),
        completedAt: past(3),
        auditEvidencePortfolioTarget: '/evidence/2026/08/safeguarding_signoff.pdf',
      },
      {
        id: generateId(),
        incidentId: inc.id,
        incident: inc,
        residentId: r.id,
        resident: r,
        title: `Medication review for ${r.identityCode}`,
        correctiveActionPlan: 'Review medication charts and adjust dosages as per GP guidance.',
        assignedOwner: 'Dr. Patel',
        status: ActionStatus.PENDING,
        dueDate: future(5),
        completedAt: null,
        auditEvidencePortfolioTarget: '/evidence/2026/08/medication_review.pdf',
      },
      {
        id: generateId(),
        incidentId: inc.id,
        incident: inc,
        residentId: r.id,
        resident: r,
        title: `Monthly falls risk assessment for ${r.identityCode}`,
        correctiveActionPlan: 'Complete Waterlow and environmental safety check.',
        assignedOwner: 'Nurse John',
        status: ActionStatus.PENDING,
        dueDate: past(2),
        completedAt: null,
        auditEvidencePortfolioTarget: '/evidence/2026/08/falls_assessment.xlsx',
      },
    ];
  });

  // ─── AUDITS ──────────────────────────────────────────────
  const audits: Audit[] = [
    {
      id: generateId(),
      title: 'Medication Administration Audit',
      category: 'Medication',
      scheduledDate: future(10),
      completedDate: null,
      findings: 'Incomplete MAR entries found on 2 occasions.',
      actionsRequired: 'Retrain staff on MAR completion; increase spot checks.',
      status: AuditStatus.SCHEDULED,
      evidenceUrl: null,
    },
    {
      id: generateId(),
      title: 'Infection Prevention & Control Audit',
      category: 'IPC',
      scheduledDate: past(5),
      completedDate: past(3),
      findings: 'Hand hygiene compliance at 92%, all PPE available.',
      actionsRequired: 'None – outstanding performance.',
      status: AuditStatus.COMPLETED,
      evidenceUrl: '/evidence/2026/08/ipc_audit.pdf',
    },
  ];

  // ─── TRAINING ─────────────────────────────────────────────
  const training: TrainingRecord[] = [
    {
      id: generateId(),
      staffName: 'Sarah Mitchell',
      staffRole: 'Registered Manager',
      courseName: 'Safeguarding Adults Level 3',
      completionDate: past(30),
      expiryDate: future(330),
      status: TrainingStatus.CURRENT,
      certificateUrl: null,
    },
    {
      id: generateId(),
      staffName: 'Nurse John',
      staffRole: 'Clinical Lead',
      courseName: 'Medication Administration Refresher',
      completionDate: past(400),
      expiryDate: past(10),
      status: TrainingStatus.EXPIRED,
      certificateUrl: null,
    },
    {
      id: generateId(),
      staffName: 'Care Assistant Emma',
      staffRole: 'Care Assistant',
      courseName: 'Basic Life Support',
      completionDate: past(60),
      expiryDate: future(300),
      status: TrainingStatus.CURRENT,
      certificateUrl: null,
    },
  ];

  // ─── POLICIES ─────────────────────────────────────────────
  const policies: Policy[] = [
    {
      id: generateId(),
      title: 'Safeguarding Adults Policy',
      category: 'Safeguarding',
      version: '3.2',
      lastReviewed: past(60),
      nextReview: future(300),
      status: PolicyStatus.CURRENT,
      documentUrl: '/policies/safeguarding_v3.2.pdf',
    },
    {
      id: generateId(),
      title: 'Infection Prevention & Control Policy',
      category: 'IPC',
      version: '2.0',
      lastReviewed: past(400),
      nextReview: past(10),
      status: PolicyStatus.OVERDUE,
      documentUrl: null,
    },
  ];

  // ─── RISKS ─────────────────────────────────────────────────
  const risks: Risk[] = [
    {
      id: generateId(),
      description: 'High staff turnover in care assistant roles.',
      category: 'Operational',
      severity: RiskSeverity.HIGH,
      mitigationAction: 'Increase recruitment efforts and improve retention incentives.',
      owner: 'HR Manager',
      reviewDate: future(30),
      status: 'OPEN',
    },
    {
      id: generateId(),
      description: 'Potential for medication errors due to high workload.',
      category: 'Clinical',
      severity: RiskSeverity.CRITICAL,
      mitigationAction: 'Implement electronic MAR; conduct fortnightly spot checks.',
      owner: 'Clinical Lead',
      reviewDate: future(14),
      status: 'OPEN',
    },
  ];

  // ─── MEETINGS ──────────────────────────────────────────────
  const meetings: Meeting[] = [
    {
      id: generateId(),
      title: 'Monthly Quality Assurance Meeting',
      date: future(5),
      attendees: ['Sarah Mitchell', 'Dr. Patel', 'Nurse John'],
      minutes: 'Review of incident trends and action plan progress.',
      actionItems: ['Update falls prevention policy', 'Schedule IPC refresher'],
      status: 'SCHEDULED',
    },
  ];

  // ─── EQUIPMENT ─────────────────────────────────────────────
  const equipment: EquipmentInspection[] = [
    {
      id: generateId(),
      equipmentName: 'Patient Hoist (Room 104)',
      serialNumber: 'H-104-2023',
      inspectionType: 'LOLER',
      lastInspection: past(100),
      nextInspection: future(260),
      status: 'PASS',
      notes: 'Hydraulic system fully functional.',
    },
    {
      id: generateId(),
      equipmentName: 'Fire Extinguisher (Corridor B)',
      serialNumber: 'FE-2024-001',
      inspectionType: 'Fire Safety',
      lastInspection: past(200),
      nextInspection: past(5),
      status: 'PENDING',
      notes: 'Monthly check due.',
    },
  ];

  // ─── COMPLAINTS ────────────────────────────────────────────
  const complaints: Complaint[] = [
    {
      id: generateId(),
      residentName: 'Margaret Smith',
      complainant: 'Family member',
      dateReceived: past(10),
      description: 'Concern about medication timing inconsistency.',
      investigationStatus: ComplaintStatus.INVESTIGATING,
      outcome: null,
      actionsTaken: null,
      closedAt: null,
    },
  ];

  // ─── COMPETENCY ACTION PLANS (with escalation fields) ──
  const plans: CompetencyActionPlan[] = [
    {
      action_id: generateId(),
      parent_session_id: 'mock-session-1',
      failed_question_ids: ['Q1'],
      staff_id: 'staff-1',
      staff_name: 'John Doe',
      deficiency_details: 'Failed Q1 on cross-contamination.',
      corrective_action_required: 'Retrain on BICSc color coding.',
      assigned_mentor_name: 'Sarah Mitchell',
      daily_verification_checks: { day_1_signed: false, day_1_notes: '', day_2_signed: false, day_2_notes: '', day_3_signed: false, day_3_notes: '' },
      weekly_manager_review: { week_1_passed: false, week_1_verified_by: '', week_1_notes: '', week_2_passed: false, week_2_verified_by: '', week_2_notes: '' },
      monthly_governance_signoff: { is_signed_off: false, signed_off_by_manager: '', sign_off_timestamp: null, final_evidence_document_url: null },
      plan_status: 'Active Remediation',
      created_at: now,
      updated_at: now,
      due_dates: { daily: [future(1), future(2), future(3)], weekly: [future(5), future(7)], monthly: future(30) },
      actual_completed_at: null,
      actual_chased_at: null,
      chase_deadline: future(4),
      closure_deadline: future(7),
    },
  ];

  // ─── WRITE TO LOCALSTORAGE ──────────────────────────────
  localStorage.setItem('rmacs_compliance_residents', JSON.stringify(residents));
  localStorage.setItem('rmacs_compliance_incidents', JSON.stringify(incidents));
  localStorage.setItem('rmacs_compliance_tasks', JSON.stringify(tasks));
  localStorage.setItem('rmacs_compliance_audits', JSON.stringify(audits));
  localStorage.setItem('rmacs_compliance_training', JSON.stringify(training));
  localStorage.setItem('rmacs_compliance_policies', JSON.stringify(policies));
  localStorage.setItem('rmacs_compliance_risks', JSON.stringify(risks));
  localStorage.setItem('rmacs_compliance_meetings', JSON.stringify(meetings));
  localStorage.setItem('rmacs_compliance_equipment', JSON.stringify(equipment));
  localStorage.setItem('rmacs_compliance_complaints', JSON.stringify(complaints));
  localStorage.setItem('rmacs_competency_remediation', JSON.stringify(plans));
}